import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { styled } from "@mui/material/styles";
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress } from "@mui/material";
import PrimaryTextField from "../../../../Inputs/PrimaryTextField/PrimaryTextField";
import useOfferCalculations from "../../../../../hooks/useOfferCalculations/useOfferCalculations";
import { formatCurrency, formatPercent } from "../../../../../utils/formatHelpers";
import { CarrierService } from "../../../../../services/CarrierService";
import { AlertContext } from "../../../../Dashboard/Dashboard";

const StyledTableCell = styled(TableCell)(() => ({
  border: 0,
  padding: "10px 16px"
}));

const StyledTableHeaderCell = styled(TableCell)(() => ({
  paddingBottom: "2px",
  paddingRight: "40px",
  "&:last-child": {
    paddingRight: 0
  }
}));

const StyledLastTableCell = styled(TableCell)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette["black_solid"]["50"]}`,
  borderBottom: 0,
  padding: "16px 16px"
}));

export default function CarrierOffersModalTargetPay({ activeLoad }) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const [commissionVariables, setCommissionVariables] = useState({});
  const {
    customerCharges,
    targetPay,
    targetProfit,
    targetMargin,
    offerAmount,
    setOfferAmount,
    offerProfit,
    offerMargin,
    maxPay,
    maxProfit,
    maxMargin,
    difference
  } = useOfferCalculations(activeLoad, commissionVariables);
  const [isLoading, setIsLoading] = useState(false);

  const getCellColor = (value) => {
    if (value >= 0) {
      return "black_solid.80";
    } else {
      return "error.60";
    }
  };

  useEffect(() => {
    if (activeLoad.id) {
      setIsLoading(true);
      CarrierService.GetCarrierCommissionValues(instance).then(res => {
        setCommissionVariables(res);
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }
  }, []);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="h3" color="primary.60">
          Target Pay Calculator
        </Typography>
        <Box sx={{ margin: "0 20px 0 auto", display: "flex", alignItems: "center" }}>
          <Typography variant="subhead2" color="black_solid.80">
            Customer Charge (LH + FSC):
          </Typography>
          <Typography variant="subhead1" color="black_solid.80" sx={{ marginLeft: "5px" }}>
            {formatCurrency(customerCharges, 2)}
          </Typography>
        </Box>
      </Box>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", margin: "60px 200px" }}>
          <CircularProgress sx={{ color: "primary.60" }} size={40} />
        </Box>
      ) : (
        <Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableHeaderCell></StyledTableHeaderCell>
                  <StyledTableHeaderCell></StyledTableHeaderCell>
                  <StyledTableHeaderCell>
                    <Typography variant="body2" color="black_solid.60">
                      Gross Profit
                    </Typography>
                  </StyledTableHeaderCell>
                  <StyledTableHeaderCell>
                    <Typography variant="body2" color="black_solid.60">
                      Margin
                    </Typography>
                  </StyledTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <StyledTableCell>
                    <Typography variant="subhead2" color="black_solid.80">
                      Target Pay
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="subhead2" color="black_solid.80">
                      {formatCurrency(targetPay, 2)}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="subhead2" color={getCellColor(targetProfit)}>
                      {formatCurrency(targetProfit, 2)}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="subhead2" color={getCellColor(targetMargin)}>
                      {formatPercent(targetMargin, 2)}
                    </Typography>
                  </StyledTableCell>
                </TableRow>
                <TableRow>
                  <StyledTableCell>
                    <Typography variant="subhead2" color="black_solid.80">
                      Dispatch Offer
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell sx={{ paddingLeft: "9px" }}>
                    <PrimaryTextField
                      onChange={setOfferAmount}
                      value={offerAmount}
                      isNumber
                      width="100px"
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="subhead1" color={getCellColor(offerProfit)}>
                      {formatCurrency(offerProfit, 2)}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="subhead1" color={getCellColor(offerMargin)}>
                      {formatPercent(offerMargin, 2)}
                    </Typography>
                  </StyledTableCell>
                </TableRow>
                <TableRow>
                  <StyledTableCell>
                    <Typography variant="subhead2" color="black_solid.80">
                      Max Pay
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="subhead2" color="black_solid.80">
                      {formatCurrency(maxPay, 2)}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="subhead2" color={getCellColor(maxProfit)}>
                      {formatCurrency(maxProfit, 2)}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="subhead2" color={getCellColor(maxMargin)}>
                      {formatPercent(maxMargin, 2)}
                    </Typography>
                  </StyledTableCell>
                </TableRow>
                <TableRow>
                  <StyledTableCell>
                    <Typography variant="subhead2" color="black_solid.80">
                      Difference
                    </Typography>
                  </StyledTableCell>
                  <StyledLastTableCell>
                    <Typography variant="subhead1" color={getCellColor(difference)}>
                      {formatCurrency(difference, 2)}
                    </Typography>
                  </StyledLastTableCell>
                  <StyledLastTableCell></StyledLastTableCell>
                  <StyledLastTableCell></StyledLastTableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}

CarrierOffersModalTargetPay.propTypes = {
  activeLoad: PropTypes.object.isRequired
};